import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api, getCurrentUserId } from '../utils/api';
import { Button } from './ui/button';
import {
  LayoutGrid,
  Bell,
  Settings,
  LogOut,
  Users,
  Newspaper,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  BarChart3
} from 'lucide-react';

export default function DashboardLayout({ children, title, headerImage }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });
  const [customer, setCustomer] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  const toggleSidebar = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', newState);
  };

  useEffect(() => {
    if (user?.customer_id) {
      api.getCustomer(user.customer_id).then(setCustomer).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const notifications = await api.getNotifications();
        const userId = getCurrentUserId();
        const unread = notifications.filter(n => !n.read_by?.includes(userId)).length;
        setUnreadCount(unread);
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      }
    };

    if (user) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000); // Refresh every 30 seconds
      return () => clearInterval(interval);
    }
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { icon: LayoutGrid, label: 'My Apps', path: '/', show: true },
    { icon: Bell, label: 'Notifications', path: '/notifications', show: true },
    { icon: Newspaper, label: 'News', path: '/articles', show: true },
  ];

  // Add Analytics for admin and super_admin users
  if (user?.role === 'admin' || user?.role === 'super_admin') {
    navItems.push({
      icon: BarChart3,
      label: 'Analytics',
      path: '/analytics',
      show: true
    });
  }

  // Show install guide only if not in standalone mode
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  if (!isStandalone && user?.role === 'user') {
    navItems.push({ 
      icon: () => <span className="text-lg">📱</span>, 
      label: 'Install App', 
      path: '/install', 
      show: true 
    });
  }

  // Admin pages should not be affected by customer background color
  const isAdminPage = title?.includes('Admin') || user?.role === 'admin';
  const backgroundStyle = isAdminPage 
    ? { backgroundColor: '#F8FAFC' } 
    : { backgroundColor: customer?.background_color || '#F8FAFC' };

  return (
    <div 
      className="min-h-screen" 
      style={backgroundStyle}
    >
      <nav className="bg-white border-b border-border sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden mr-4 p-2 rounded-md hover:bg-accent"
                data-testid="mobile-menu-toggle"
              >
                {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
              <div className="flex items-center gap-3">
                <img src="/logo.svg" alt="Taptile" className="w-8 h-8" />
                <h1 className="text-xl font-heading font-bold text-primary">Taptile</h1>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-foreground">{user?.name}</p>
                <p className="text-xs text-muted-foreground capitalize">{user?.role?.replace('_', ' ')}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                data-testid="logout-button"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Log Out
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex max-w-full mx-auto">
        <aside
          className={`${
            sidebarOpen ? 'block' : 'hidden'
          } lg:block ${sidebarCollapsed ? 'lg:w-16' : 'lg:w-64'} bg-white border-r border-border min-h-[calc(100vh-4rem)] transition-all duration-300 relative`}
        >
          {/* Collapse Toggle Button - Desktop Only */}
          <button
            onClick={toggleSidebar}
            className="hidden lg:flex absolute -right-3 top-6 bg-white border border-border rounded-full p-1 hover:bg-accent transition-colors z-10 shadow-md"
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? (
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            ) : (
              <ChevronLeft className="w-4 h-4 text-muted-foreground" />
            )}
          </button>

          <div className="p-4 space-y-2">
            {navItems
              .filter((item) => item.show)
              .map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className="flex items-center space-x-3 px-4 py-3 rounded-lg hover:bg-accent transition-colors group"
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  title={sidebarCollapsed ? item.label : ''}
                >
                  <div className="relative flex-shrink-0">
                    <item.icon className="w-5 h-5 text-muted-foreground" />
                    {item.label === 'Notifications' && unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </div>
                  {!sidebarCollapsed && (
                    <span className="text-sm font-medium">{item.label}</span>
                  )}
                  {sidebarCollapsed && (
                    <span className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                      {item.label}
                    </span>
                  )}
                </Link>
              ))}
          </div>
        </aside>

        <main className={`flex-1 ${sidebarCollapsed ? 'max-w-full' : 'max-w-7xl mx-auto'}`}>
          {/* Header Image - Full Width */}
          {headerImage}
          
          <div className="p-6 lg:p-8">
            <div className="mb-8">
              <h2 
                className="text-3xl font-heading font-bold text-foreground"
                style={{ color: customer?.primary_color || '#2563EB' }}
              >
                {title === 'My Apps' ? (customer?.header_text || customer?.name || 'My Apps') : title}
              </h2>
            </div>
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}